import type { SupabaseClient } from "@supabase/supabase-js";

import { isMissingPolicyRpc } from "./alert-channel-policy";

// Field-consent acknowledgement roster (PRD-001T durability + host auto-policy).
//
// Host contract (PR #278 / migration 20260812120000_field_consent_auto_policy.sql):
//   get_field_consent_ack_roster(p_account_id uuid)
//   policy.view gated; one row per active guard/supervisor/admin membership.
//
// Columns:
//   membership_id, member_ref, member_display_name, member_role,
//   consent_policy_version_id, consent_version, policy_title, policy_effective_at,
//   accepted_at, missing_ack
//
// consent_version is always the CURRENT active policy version (not the version a member
// once accepted). Acceptance is accepted_at set / missing_ack false. Do not treat
// consent_version presence as "they accepted".

export const GET_FIELD_CONSENT_ACK_ROSTER_RPC = "get_field_consent_ack_roster";

/** @deprecated Use GET_FIELD_CONSENT_ACK_ROSTER_RPC; kept for tests that import either. */
export const LIST_FIELD_CONSENT_ACKS_RPC = GET_FIELD_CONSENT_ACK_ROSTER_RPC;

export const FIELD_DUTY_CONSENT_KEY = "field_duty";

export interface ConsentAckRow {
  readonly membershipId: string;
  readonly memberRef: string | null;
  readonly memberDisplayName: string | null;
  readonly role: string;
  /** Always the current active policy version for the account when the host returned a row. */
  readonly consentVersion: string | null;
  readonly acceptedAt: string | null;
  readonly missingAck: boolean;
}

export type ConsentAckLoad =
  | {
      readonly status: "ready";
      /** Current active field_duty version from roster rows (or null if none returned). */
      readonly activeVersion: string | null;
      readonly rows: readonly ConsentAckRow[];
    }
  | { readonly status: "unavailable" };

export const CONSENT_ACKS_UNAVAILABLE =
  "Acceptance tracking isn't available on this server yet. Publish history above is unaffected.";

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

export function mapConsentAckRow(raw: unknown): ConsentAckRow | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const membershipId = asString(row.membership_id);
  if (membershipId === null) return null;
  const role = asString(row.member_role) ?? asString(row.role) ?? "member";
  const acceptedAt = asString(row.accepted_at);
  const missingFlag = asBoolean(row.missing_ack);
  const missingAck = missingFlag ?? acceptedAt === null;
  return {
    membershipId,
    memberRef: asString(row.member_ref),
    memberDisplayName: asString(row.member_display_name),
    role,
    consentVersion: asString(row.consent_version),
    acceptedAt,
    missingAck,
  };
}

/**
 * Never throws for a server that simply lacks the RPC: that reads as `unavailable`.
 * A real failure still throws so the panel shows its error state rather than a silently empty roster.
 */
export async function fetchFieldConsentAcks(
  client: SupabaseClient,
  accountId: string,
): Promise<ConsentAckLoad> {
  const { data, error } = await client.rpc(GET_FIELD_CONSENT_ACK_ROSTER_RPC, {
    p_account_id: accountId,
  });
  if (error !== null) {
    if (isMissingPolicyRpc(error)) {
      return { status: "unavailable" };
    }
    // Host raises when no active policy exists. Treat as ready with empty rows so the summary
    // can say there is nothing to accept yet, rather than "server feature missing".
    const message = typeof error.message === "string" ? error.message : "";
    if (/no active field_duty consent policy/i.test(message)) {
      return { status: "ready", activeVersion: null, rows: [] };
    }
    throw new Error("Could not load field consent acceptances.");
  }

  const rows: ConsentAckRow[] = [];
  let activeVersion: string | null = null;
  if (Array.isArray(data)) {
    for (const raw of data) {
      const row = mapConsentAckRow(raw);
      if (row !== null) {
        rows.push(row);
        if (activeVersion === null && row.consentVersion !== null) {
          activeVersion = row.consentVersion;
        }
      }
    }
  }
  return { status: "ready", activeVersion, rows };
}

export interface ConsentAckRoster {
  readonly accepted: readonly ConsentAckRow[];
  readonly missing: readonly ConsentAckRow[];
}

/** Split using host missing_ack / accepted_at. Equality on consent_version alone is not enough. */
export function splitConsentAckRoster(rows: readonly ConsentAckRow[]): ConsentAckRoster {
  const accepted: ConsentAckRow[] = [];
  const missing: ConsentAckRow[] = [];
  for (const row of rows) {
    if (row.missingAck || row.acceptedAt === null) {
      missing.push(row);
    } else {
      accepted.push(row);
    }
  }
  return { accepted, missing };
}

/** "Jo Rivera", else token-safe member_ref, else a role-scoped placeholder. Never a raw user id. */
export function consentAckMemberLabel(row: ConsentAckRow): string {
  return row.memberDisplayName ?? row.memberRef ?? `${row.role} member`;
}

export function formatConsentAckedAt(acceptedAt: string | null): string {
  if (acceptedAt === null) return "Not accepted";
  const parsed = new Date(acceptedAt);
  return Number.isNaN(parsed.getTime()) ? "Not accepted" : parsed.toLocaleString();
}

export function summarizeConsentAcks(roster: ConsentAckRoster, activeVersion: string | null): string {
  const total = roster.accepted.length + roster.missing.length;
  if (activeVersion === null) {
    return "No active field consent policy is published, so there is nothing for officers to accept yet.";
  }
  if (total === 0) {
    return `No active members to track against ${activeVersion}.`;
  }
  return `${roster.accepted.length} of ${total} active members have accepted ${activeVersion}.`;
}
